import type { ReactNode } from "react";

/**
 * Small, dependency-free Markdown renderer shared with the Finance Assistant.
 * It follows New College Admin's ChatMarkdown pattern and deliberately supports
 * the response shapes the assistant emits: headings, lists, inline emphasis,
 * links, code, separators, and GitHub-style pipe tables.
 */
export function ChatMarkdown({ content }: { content: string }): JSX.Element {
  const lines = content.split("\n");
  const nodes: ReactNode[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index]!;
    if (line.trim().startsWith("|") && index + 1 < lines.length && isTableSeparator(lines[index + 1]!)) {
      const headers = parseTableRow(line);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && lines[index]!.trim().startsWith("|") && !isTableSeparator(lines[index]!)) {
        rows.push(parseTableRow(lines[index]!));
        index += 1;
      }
      nodes.push(
        <div key={`table-${index}`} className="my-3 max-w-full overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-max min-w-full table-auto border-collapse text-left text-xs sm:text-sm">
            <thead className="bg-slate-100">
              <tr>
                {headers.map((cell, cellIndex) => (
                  <th key={cellIndex} className="border-b border-r border-slate-200 px-3 py-2 font-semibold text-slate-800 last:border-r-0">
                    {renderInline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b border-slate-100 last:border-b-0 odd:bg-white even:bg-slate-50/60">
                  {headers.map((_, cellIndex) => (
                    <td key={cellIndex} className="border-r border-slate-100 px-3 py-2 align-top text-slate-700 last:border-r-0">
                      {renderInline(row[cellIndex] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (index < lines.length) {
        const match = lines[index]!.match(/^\s*[-*]\s+(.+)/);
        if (!match) break;
        items.push(match[1]!);
        index += 1;
      }
      nodes.push(<ul key={`ul-${index}`} className="my-2 list-disc space-y-1 pl-5">{items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item)}</li>)}</ul>);
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (index < lines.length) {
        const match = lines[index]!.match(/^\s*\d+\.\s+(.+)/);
        if (!match) break;
        items.push(match[1]!);
        index += 1;
      }
      nodes.push(<ol key={`ol-${index}`} className="my-2 list-decimal space-y-1 pl-5">{items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item)}</li>)}</ol>);
      continue;
    }

    if (!line.trim()) nodes.push(<div key={index} className="h-2" />);
    else if (/^---+$/.test(line.trim())) nodes.push(<hr key={index} className="my-3 border-slate-200" />);
    else {
      const heading = line.match(/^(#{1,3})\s+(.+)/);
      nodes.push(heading
        ? <p key={index} className="mb-1 mt-3 font-bold text-slate-900">{renderInline(heading[2]!)}</p>
        : <p key={index} className="leading-6">{renderInline(line)}</p>);
    }
    index += 1;
  }

  return <div className="max-w-none overflow-hidden">{nodes}</div>;
}

function renderInline(text: string): ReactNode[] {
  return text
    .split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^)]+\))/g)
    .filter(Boolean)
    .map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) return <strong key={index} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
      if (part.startsWith("`") && part.endsWith("`")) return <code key={index} className="rounded bg-slate-100 px-1 py-0.5 text-[0.9em] text-slate-800">{part.slice(1, -1)}</code>;
      const link = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/);
      if (link) return <a key={index} href={link[2]} target="_blank" rel="noreferrer" className="font-medium text-blue-600 underline underline-offset-2">{link[1]}</a>;
      return part;
    });
}

function isTableSeparator(line: string): boolean {
  return /^\|[\s|:-]+\|\s*$/.test(line.trim()) && line.includes("-");
}

function parseTableRow(line: string): string[] {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((cell) => cell.trim());
}
